import { useEffect, useRef } from 'react'

const SELECTOR_ENFOCABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Accesibilidad de modales (P-100, RNF-10): al abrir un diálogo, mueve el foco adentro y lo
 * atrapa (Tab/Shift+Tab no se escapan al resto de la página); Escape lo cierra; al cerrarse,
 * regresa el foco a donde estaba antes de abrirlo. Úsalo en el elemento ".panel" del modal:
 *
 *   const panelRef = useTrampaDeFoco<HTMLDivElement>(true, alCerrar)
 *   <div className="modal" onClick={alCerrar}>
 *     <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="titulo-x"
 *          className="panel" onClick={(e) => e.stopPropagation()}>
 *       <h2 id="titulo-x">...</h2>
 *     </div>
 *   </div>
 *
 * `activo` distingue dos formas de montar un modal en esta SPA: cuando el modal es su propio
 * componente de función (se monta y desmonta con él), pásale siempre `true` — el efecto corre
 * una vez, al montar. Cuando el modal se renderiza condicionalmente DENTRO de un componente que
 * sigue vivo (p. ej. `{abierta && <div className="modal">…</div>}`), pásale la condición misma
 * (`abierta != null`): así el efecto se vuelve a armar cada vez que el modal se abre, en vez de
 * solo la primera vez que el componente contenedor se montó.
 */
export function useTrampaDeFoco<T extends HTMLElement>(activo: boolean, alCerrar: () => void) {
  const ref = useRef<T>(null)
  // "Última versión" de alCerrar: evita que un alCerrar recreado en cada render (común aquí,
  // suele ser un arrow function inline) reinicie el efecto y le vuelva a mover el foco al panel.
  const alCerrarRef = useRef(alCerrar)
  alCerrarRef.current = alCerrar

  useEffect(() => {
    if (!activo) return

    const elementoPrevio = document.activeElement;
    (ref.current?.querySelector<HTMLElement>(SELECTOR_ENFOCABLE) ?? ref.current)?.focus()

    function alTeclado(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        alCerrarRef.current()
        return
      }

      if (evento.key !== 'Tab' || !ref.current) return

      const enfocables = Array.from(ref.current.querySelectorAll<HTMLElement>(SELECTOR_ENFOCABLE))
      if (enfocables.length === 0) return

      const primero = enfocables[0]
      const ultimo = enfocables[enfocables.length - 1]

      if (evento.shiftKey && document.activeElement === primero) {
        evento.preventDefault()
        ultimo.focus()
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault()
        primero.focus()
      }
    }

    document.addEventListener('keydown', alTeclado)
    return () => {
      document.removeEventListener('keydown', alTeclado)
      if (elementoPrevio instanceof HTMLElement) elementoPrevio.focus()
    }
  }, [activo])

  return ref
}
